// Pagina di login con react-hook-form + zod + magic link
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Mail, Lock, Loader2 } from 'lucide-react'
import { Input } from '../../components/ui/Input'
import { Button } from '../../components/ui/Button'
import { Alert } from '../../components/ui/Alert'
import { useAuth } from '../../hooks/useAuth'
import { useAuthStore } from '../../stores/auth.store'
import api from '../../lib/api'

const loginSchema = z.object({
  email: z.string().email('Inserisci un indirizzo email valido'),
  password: z.string().min(6, 'La password deve avere almeno 6 caratteri'),
})

type LoginFormData = z.infer<typeof loginSchema>

export function LoginPage() {
  const { login, isLoginLoading, loginError } = useAuth()
  const store = useAuthStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const magicToken = searchParams.get('token')
  const [magicLoading, setMagicLoading] = useState(Boolean(magicToken))
  const [magicError, setMagicError] = useState<string | null>(null)

  useEffect(() => {
    if (!magicToken) return
    api
      .get<{
        accessToken: string
        refreshToken: string
        expiresIn: number
        user: { id: string; email: string; firstName: string; lastName: string; role: string; isActive: boolean }
      }>(`/auth/magic-link/verify/${magicToken}`)
      .then((res) => {
        const { accessToken, refreshToken, expiresIn, user } = res.data
        store.setAuth({ accessToken, refreshToken, expiresIn }, user as any)
        navigate('/', { replace: true })
      })
      .catch(() => {
        setMagicError("Link non valido o scaduto. Contatta l'amministratore per riceverne uno nuovo.")
        setMagicLoading(false)
      })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magicToken])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = (data: LoginFormData) => {
    login({ email: data.email, password: data.password })
  }

  const errorMessage =
    loginError instanceof Error ? loginError.message : loginError ? 'Credenziali non valide' : null

  // Se c'è un magic token, mostra solo lo stato verifica
  if (magicToken) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--fc-background)] p-4">
        <div className="w-full max-w-sm text-center">
          <img src="/logo.png" alt="KleisNote" style={{ height: 48, width: 'auto', maxWidth: 160, margin: '0 auto 12px' }} />
          {magicLoading && (
            <div className="space-y-3">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-[var(--fc-primary)]" />
              <p className="text-sm text-[var(--fc-foreground-muted)]">Accesso in corso...</p>
            </div>
          )}
          {magicError && (
            <div className="space-y-4">
              <Alert variant="error">{magicError}</Alert>
              <Button variant="secondary" onClick={() => navigate('/login', { replace: true })}>
                Torna al login
              </Button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--fc-background)] p-4">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-6 text-center">
          <img
            src="/logo.png"
            alt="KleisNote"
            style={{ height: 48, width: 'auto', maxWidth: 160, margin: '0 auto 12px' }}
          />
          <h1 className="text-xl font-bold text-[var(--fc-foreground)]">KleisNote</h1>
          <p className="mt-0.5 text-xs text-[var(--fc-foreground-muted)]">Gestione interventi sul campo</p>
        </div>

        {/* Card login */}
        <div className="rounded-xl border border-[var(--fc-border)] bg-[var(--fc-surface)] p-6 shadow-[var(--fc-shadow-md)]">
          {errorMessage && (
            <Alert variant="error" className="mb-4">
              {errorMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
            <Input
              label="Email"
              type="email"
              autoComplete="email"
              leftIcon={<Mail className="h-4 w-4" />}
              error={errors.email?.message}
              required
              {...register('email')}
            />
            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              leftIcon={<Lock className="h-4 w-4" />}
              error={errors.password?.message}
              required
              {...register('password')}
            />

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-[var(--fc-foreground-muted)]">
                <input
                  type="checkbox"
                  className="rounded border-[var(--fc-border)] text-[var(--fc-primary)]"
                />
                Ricordami
              </label>
              <Link
                to="/forgot-password"
                className="text-sm text-[var(--fc-primary)] hover:underline"
              >
                Password dimenticata?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full"
              isLoading={isLoginLoading}
            >
              Accedi
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-[var(--fc-foreground-subtle)]">
          KleisNote v1.0 — &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  )
}
